import type { Edge, Node } from '@xyflow/react';

export type OrgMvpEdgeTipo = 'direta' | 'pontilhada' | 'staff' | 'projeto';

export const EDGE_TIPOS: OrgMvpEdgeTipo[] = ['direta', 'pontilhada', 'staff', 'projeto'];

export const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

export const nodeSizeOf = (node: Node) => ({
  width: typeof node.width === 'number' ? node.width : Number(node.style?.width) || 220,
  height: typeof node.height === 'number' ? node.height : Number(node.style?.height) || 118,
});

export const EDGE_TIPO_LABELS: Record<OrgMvpEdgeTipo, string> = {
  direta: 'Direta (hierarquia)',
  pontilhada: 'Pontilhada (matricial)',
  staff: 'Staff / assessoria',
  projeto: 'Projeto',
};

export const strokeDasharrayForTipo = (tipo: OrgMvpEdgeTipo): string | undefined => {
  if (tipo === 'pontilhada') return '6 6';
  if (tipo === 'staff') return '2 5';
  if (tipo === 'projeto') return '12 5';
  return undefined;
};

export const getEdgeTipo = (edge: Edge): OrgMvpEdgeTipo => {
  const tipo = (edge.data as { tipo?: unknown } | undefined)?.tipo;
  return EDGE_TIPOS.includes(tipo as OrgMvpEdgeTipo) ? (tipo as OrgMvpEdgeTipo) : 'direta';
};

/**
 * Aresta de hierarquia = ligação card→card do tipo 'direta' (ou sem tipo).
 * Pontilhada/staff/projeto e ligações envolvendo figuras/colunas ficam fora
 * dos cálculos de subordinação (contadores, colapso, ciclo, auto-layout).
 */
export const isHierarchyEdge = (edge: Edge, cardIds: Set<string>): boolean =>
  cardIds.has(edge.source) && cardIds.has(edge.target) && getEdgeTipo(edge) === 'direta';

export const getCardIds = (nodes: Node[]): Set<string> =>
  new Set(nodes.filter((node) => node.type === 'card').map((node) => node.id));

export const buildChildrenMap = (edges: Edge[], cardIds: Set<string>): Map<string, string[]> => {
  const children = new Map<string, string[]>();
  edges.forEach((edge) => {
    if (!isHierarchyEdge(edge, cardIds)) return;
    const list = children.get(edge.source);
    if (list) {
      if (!list.includes(edge.target)) list.push(edge.target);
    } else {
      children.set(edge.source, [edge.target]);
    }
  });
  return children;
};

export const countDirectReports = (edges: Edge[], cardIds: Set<string>): Map<string, number> => {
  const counts = new Map<string, number>();
  buildChildrenMap(edges, cardIds).forEach((childIds, parentId) => {
    counts.set(parentId, childIds.length);
  });
  return counts;
};

/** BFS de descendentes tolerante a ciclos em dados legados. */
export const getDescendants = (
  rootId: string,
  childrenMap: Map<string, string[]>,
): Set<string> => {
  const visited = new Set<string>();
  const queue = [...(childrenMap.get(rootId) || [])];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || visited.has(current) || current === rootId) continue;
    visited.add(current);
    queue.push(...(childrenMap.get(current) || []));
  }
  return visited;
};

/**
 * Uma nova aresta source→target cria ciclo se source já é descendente de target
 * na hierarquia (considerando apenas arestas 'direta' entre cards).
 * `ignoreEdgeId` permite simular reconexão excluindo a aresta antiga.
 */
export const wouldCreateCycle = (
  sourceId: string,
  targetId: string,
  edges: Edge[],
  cardIds: Set<string>,
  ignoreEdgeId?: string,
): boolean => {
  if (sourceId === targetId) return true;
  const relevantEdges = ignoreEdgeId ? edges.filter((edge) => edge.id !== ignoreEdgeId) : edges;
  const childrenMap = buildChildrenMap(relevantEdges, cardIds);
  return getDescendants(targetId, childrenMap).has(sourceId);
};
